import {
  buildRecommendationConditionSchema,
  parseRecommendationCondition,
} from '@biteinc/core-effect/wire/recommendation-condition';
import {
  ConditionSchemaName,
  ConditionSchemaNameHelper,
  MenuItemAffinityFormulaDescription,
  ModelType,
  RecommendationRuleEffectName,
  RecommendationRuleEffectNameHelper,
} from '@biteinc/enums';

import { recommendationRuleSchema } from './recommendation_rule_schema';

app.RecommendationRule = app.AbstractModel.extend({
  displayName() {
    if (this.attributes.description) {
      return this.attributes.description;
    }
    if (this.isNew()) {
      return this.attributes.effectName;
    }
    return `${this.attributes.effectName} #${this.id.slice(-4)}`;
  },

  displayNameHtml() {
    let title = `<span>${this.displayName()}`;

    if (this.get('isInvalid')) {
      title += ' <span class="cell-badge error" title="Invalid Menu Item">invalid</span>';
    }

    title += '</span>';

    return title;
  },

  ModelName: 'recommendationRule',
  Schema: recommendationRuleSchema,
  Type: ModelType.RecommendationRule,

  parse(data, options) {
    const response = app.AbstractModel.prototype.parse.apply(this, [data, options]);

    const recommendationEffect = response.recommendationEffect;

    // build condition schema UI
    const conditionSchemaJson = response.conditionSchema;
    let conditionSchema;

    let conditionSchemaName;
    let conditionSchemaSectionIds;
    let conditionSchemaSectionId;
    let conditionSchemaItemIds;
    let conditionTimingIds;
    if (conditionSchemaJson) {
      // infer the bureau UI model fields from the prebuilt schema the condition matches, if any
      const { name, itemIds, sectionIds, sectionId, timingIds } =
        parseRecommendationCondition(conditionSchemaJson);
      conditionSchemaItemIds = itemIds;
      conditionSchemaSectionIds = sectionIds;
      conditionSchemaSectionId = sectionId;
      conditionSchemaName = name;
      conditionTimingIds = timingIds;

      conditionSchema = JSON.stringify(response.conditionSchema, null, 2);
    } else {
      conditionSchemaName = ConditionSchemaName.None;
    }

    // the model we show to the user is different from the model in the database, so transform
    const transformedResponse = {
      _id: response._id,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      conditionSchema,
      conditionSchemaName,
      conditionSchemaSectionId,
      conditionSchemaSectionIds,
      conditionSchemaItemIds,
      conditionTimingIds,
      description: response.description,
      effectName: recommendationEffect.name,
      ...(response.affinityRunId && {
        effectAffinityRunId: response.affinityRunId,
      }),
      ...(recommendationEffect.body && {
        ...(recommendationEffect.body.formulaDescription && {
          effectFormulaDescription: recommendationEffect.body.formulaDescription,
        }),
        ...(recommendationEffect.body.itemIds && {
          effectItemIds: recommendationEffect.body.itemIds,
        }),
        ...(recommendationEffect.body.sectionId && {
          effectSectionId: recommendationEffect.body.sectionId,
        }),
        ...(recommendationEffect.body.sectionIds && {
          effectSectionIds: recommendationEffect.body.sectionIds,
        }),
        ...(recommendationEffect.body.limitItemsAfterFiltering && {
          effectLimitItemsAfterFiltering: recommendationEffect.body.limitItemsAfterFiltering,
        }),
        ...('randomSortItemsWithinSections' in recommendationEffect.body && {
          effectRandomSortItemsWithinSections:
            recommendationEffect.body.randomSortItemsWithinSections,
        }),
        ...('randomizeEffectItemIds' in recommendationEffect.body && {
          randomizeEffectItemIds: recommendationEffect.body.randomizeEffectItemIds,
        }),
        ...(recommendationEffect.body.modifierIds && {
          effectModifierIds: recommendationEffect.body.modifierIds,
        }),
      }),
      ...(response.isInvalid && {
        isInvalid: response.isInvalid,
        invalidReason: response.invalidReason,
      }),
    };

    return transformedResponse;
  },

  save(attrs, opts) {
    // the model we show to the user is different from the model in the database, so transform

    let conditionSchemaJson = null; // null so that we unset if it's not set
    if (attrs.conditionSchemaName === ConditionSchemaName.Custom) {
      if (attrs.conditionSchema) {
        conditionSchemaJson = JSON.parse(attrs.conditionSchema);
      }
    } else if (attrs.conditionSchemaName) {
      conditionSchemaJson =
        buildRecommendationConditionSchema({
          name: attrs.conditionSchemaName,
          itemIds: attrs.conditionSchemaItemIds,
          sectionIds: attrs.conditionSchemaSectionIds,
          sectionId: attrs.conditionSchemaSectionId,
          timingIds: attrs.conditionTimingIds,
        }) ?? null;
    }

    // The server validates this shape with the per-effect union in
    // packages/core-effect/src/wire/recommendation-effect.ts (makeRecommendationEffectSchema).
    const transformedAttributes = {
      conditionSchema: conditionSchemaJson,
      description: attrs.description,
      recommendationEffect: {
        name: attrs.effectName,
        body: {
          ...(attrs.effectFormulaDescription &&
            [
              RecommendationRuleEffectName.MenuItemAffinity,
              RecommendationRuleEffectName.MenuItemAffinityMagicCopyKey,
            ].includes(attrs.effectName) && {
              formulaDescription: attrs.effectFormulaDescription,
            }),
          ...(attrs.effectItemIds?.length &&
            [
              RecommendationRuleEffectName.DoNotRecommendItems,
              RecommendationRuleEffectName.RecommendItems,
              RecommendationRuleEffectName.SortItemsInSection,
            ].includes(attrs.effectName) && {
              itemIds: attrs.effectItemIds,
            }),
          ...(attrs.effectModifierIds?.length &&
            [RecommendationRuleEffectName.RecommendModifiers].includes(attrs.effectName) && {
              modifierIds: attrs.effectModifierIds,
            }),
          ...(attrs.effectSectionId &&
            [RecommendationRuleEffectName.SortItemsInSection].includes(attrs.effectName) && {
              sectionId: attrs.effectSectionId,
            }),
          ...(attrs.effectSectionIds?.length &&
            [
              RecommendationRuleEffectName.DoNotRecommendItemsInSection,
              RecommendationRuleEffectName.RecommendItemsInSection,
              RecommendationRuleEffectName.SortItemsInSectionsRandomly,
            ].includes(attrs.effectName) && {
              sectionIds: attrs.effectSectionIds,
            }),
          ...([
            RecommendationRuleEffectName.HeuristicsV2,
            RecommendationRuleEffectName.MenuItemAffinity,
            RecommendationRuleEffectName.MenuItemAffinityMagicCopyKey,
            RecommendationRuleEffectName.RecommendItems,
            RecommendationRuleEffectName.RecommendModifiers,
            RecommendationRuleEffectName.RecommendItemsInSection,
            RecommendationRuleEffectName.RecommendGuestPreviouslyOrderedItems,
            RecommendationRuleEffectName.RecommendGuestFavoriteItems,
            RecommendationRuleEffectName.RecommendGuestPredicationsOnPreviouslyOrderedItems,
          ].includes(attrs.effectName) && {
            limitItemsAfterFiltering: attrs.effectLimitItemsAfterFiltering ?? 2,
          }),
          ...('effectRandomSortItemsWithinSections' in attrs &&
            [RecommendationRuleEffectName.HeuristicsV2].includes(attrs.effectName) && {
              randomSortItemsWithinSections: attrs.effectRandomSortItemsWithinSections,
            }),
          ...(attrs.randomizeEffectItemIds &&
            [
              RecommendationRuleEffectName.RecommendItems,
              RecommendationRuleEffectName.RecommendModifiers,
              RecommendationRuleEffectName.RecommendItemsInSection,
            ].includes(attrs.effectName) && {
              randomizeEffectItemIds: attrs.randomizeEffectItemIds,
            }),
        },
      },
      ...(Object.prototype.hasOwnProperty.call(attrs, 'effectAffinityRunId') &&
        attrs.effectName === RecommendationRuleEffectName.MenuItemAffinityMagicCopyKey && {
          affinityRunId: attrs.effectAffinityRunId || null,
        }),
    };

    if (!Object.keys(transformedAttributes.recommendationEffect.body).length) {
      // maitred doesn't support empty objects in the request body, so delete empty bodies
      delete transformedAttributes.recommendationEffect.body;
    }

    app.AbstractModel.prototype.save.apply(this, [transformedAttributes, opts]);
  },

  url() {
    return `/api/v2/recommendation-rules/${this.id || ''}`;
  },

  getFieldCollection(field, subProperty, _includeAllValues, _keyModel) {
    const fieldName = (subProperty ? `${subProperty}.` : '') + field;
    switch (fieldName) {
      case 'conditionSchemaName':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: ConditionSchemaName,
          nameGenerator: ConditionSchemaNameHelper.name,
        });
      case 'effectName':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: RecommendationRuleEffectName,
          nameGenerator: RecommendationRuleEffectNameHelper.name,
        });
      case 'effectSectionId':
      case 'effectSectionIds':
      case 'conditionSchemaSectionIds':
      case 'conditionSchemaSectionId':
        return app.menuSectionList;
      case 'conditionTimingIds':
        return app.menuTimingList;
      case 'conditionSchemaItemIds':
      case 'effectItemIds':
        return app.menuItemList;
      case 'effectAffinityRunId':
        return app.menuItemAffinityRunList;
      case 'effectModifierIds':
        return app.modList;
      case 'effectFormulaDescription':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemAffinityFormulaDescription,
          nameGenerator: (name) => {
            switch (name) {
              case MenuItemAffinityFormulaDescription.OrderedItemCount:
                return 'Ordered Item Count';
              default:
                throw new Error(`unrecognized menu item affinity formula description : ${name}`);
            }
          },
        });
    }
    return null;
  },
});
